import React, { useState, useEffect } from "react";
import Select from "react-select";

const AsyncDropdown = ({
    label,
    name,
    apiCallFunction, // Function to fetch data based on input
    placeholder = "Select",
    required = false,
    needSearch = false,
    debounceTime = 300, // Debounce for API calls
    onChange,
    ...props
}) => {
    const [inputValue, setInputValue] = useState(""); // Tracks user's input
    const [options, setOptions] = useState([]); // Stores dropdown options
    const [selectedValue, setSelectedValue] = useState(null);
    const [isLoading, setIsLoading] = useState(false); // For loader while fetching

    // Function to handle debounce for input changes
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            if (inputValue && apiCallFunction) {
                fetchOptions(inputValue);
            }
        }, debounceTime);

        return () => clearTimeout(delayDebounce); // Cleanup timeout
    }, [inputValue]);

    // Function to fetch options via API
    const fetchOptions = async (input) => {
        try {
            setIsLoading(true);
            const response = await apiCallFunction(input); // Call the provided function
            if (Array.isArray(response)) {
                setOptions(response);
            } else {
                setOptions([]);
            }
        } catch (error) {
            console.error("Error fetching options:", error);
            setOptions([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (selectedOption) => {
        setSelectedValue(selectedOption);
        if (onChange) {
            onChange(selectedOption); // Call the parent onChange if provided
        }
    };

    return (
        <div className="form-control w-full">
            {/* Label */}
            {label && (
                <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}
            <Select
                options={options}
                value={selectedValue}
                onChange={handleChange}
                onInputChange={(value) => setInputValue(value)} // Update inputValue on user typing
                classNamePrefix="react-select"
                placeholder={placeholder}
                isSearchable={needSearch}
                isLoading={isLoading} // Loader for async calls
                noOptionsMessage={() => "No options available"}
                styles={{
                    control: (base, state) => ({
                        ...base,
                        minHeight: '44px',
                        borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
                        boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.1)' : 'none',
                        '&:hover': {
                            borderColor: '#9ca3af'
                        },
                        borderRadius: '8px',
                        transition: 'all 0.2s ease'
                    }),
                    menu: (base) => ({
                        ...base,
                        borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        border: '1px solid #e5e7eb'
                    }),
                    option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#f3f4f6' : 'white',
                        color: state.isSelected ? 'white' : '#374151',
                        '&:active': {
                            backgroundColor: '#1d4ed8'
                        }
                    })
                }}
                {...props}
            />
        </div>
    );
};

export default AsyncDropdown;
